import { FormikErrors, FormikProps, withFormik } from 'formik'
import { t } from 'i18next'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'
import { useNavigate } from '@lib/react-router-dom'

import {
  useDemoTradingAccountFetch,
  useNewTradingAccountDemoData,
  usePreselectEffect,
} from '@pages/Traders-Room/Trading-Accounts/NewTradingAccount/Demo/hooks'
import { DynamicLeverageModal } from '@pages/Traders-Room/Trading-Accounts/modals/DynamicLeverage.modal'

import { useModal } from '@context/ModalsContext'
import { usePathHistoryContext } from '@context/PathHistoryContext'

import { Button } from '@ui/components/Button'
import { PopoverInput } from '@ui/components/Popover'

import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { useWindowResize } from '@utils/domUtils'
import { FormSubmitValues } from '@utils/formValidation'

import { CreateDemoTradingAccount } from '@model/CreateTradingAccount'
import { isLeverageTypeDynamic } from '@model/LeverageTypeDto'
import { NameDto } from '@model/NameDto'
import { isTMTPlatformType, isTradingViewPlatformType } from '@model/PlatformTypeEnum'

import { usePasswordValidation } from '@global/PasswordValidation/PasswordValidation'
import { createFormNumericField } from '@global/formField/FormNumericField'
import { createFormPasswordField } from '@global/formField/FormPasswordField'
import { FormTemplate } from '@global/templates/FormTemplate'
import { TransactionFormActionButtonTemplate } from '@global/templates/TransactionFormActionButtonTemplate'

import { FieldWrapper } from '@shared/components/FieldWrapper'
import { Loading } from '@shared/components/Loading/Loading'
import { ActionModal } from '@shared/modals/Action.modal'

import { useLeverageOptions } from '../../hooks/useLeverageOptions'
import { CreateDemoTradingAccountFormValues, OuterProps } from './CreateDemoTradingAccount.types'
import { useModals } from './CreateDemoTradingAccountModals'

import styles from './CreateDemoTradingAccountForm.module.scss'

const PasswordFormField = createFormPasswordField<CreateDemoTradingAccountFormValues>()
const NumericFormField = createFormNumericField<CreateDemoTradingAccountFormValues>()

const DEFAULT_VIRTUAL_DEPOSIT = 100000

const CreateDemoTradingAccountFormUI: React.FC<
  FormikProps<CreateDemoTradingAccountFormValues> & OuterProps
> = (props) => {
  const { handleSubmit, values, setFieldValue, isValid, setValues } = props
  const { isSubmitting, dirty } = props

  const { t } = useTranslation()
  const navigate = useNavigate()
  const isMobile = useWindowResize()
  const { navigateToPreviousPath } = usePathHistoryContext()

  const [isLoading, setIsLoading] = useState(false)

  const { leverages, leverageOptions: rawLeverageOptions } = useLeverageOptions(
    values.platformType?.id,
    values.leverageType?.id
  )

  const leverageOptions = useMemo(
    () =>
      rawLeverageOptions.map((lev) => ({
        name: lev.label,
        id: lev.value,
      })),
    [rawLeverageOptions]
  )

  const dataProps = useNewTradingAccountDemoData(props)
  const { subtitle, disabledFields, isLeverageDisabled, isCurrencyDisabled } = dataProps
  const { isClassificationTypeVisible } = dataProps

  const cancelModal = useModal(ActionModal, {
    onConfirm: () => {
      if (isMobile) {
        navigate('/dashboard/traders-room/trading-accounts', { replace: true })
      } else {
        navigateToPreviousPath({ state: { fromAddDemoTradingAccountForm: true } })
      }
    },
  })

  const modalsProps = useModals(props)
  const dynamicLeverageModal = useModal(DynamicLeverageModal)

  useDemoTradingAccountFetch(props, modalsProps)
  usePreselectEffect(props, dataProps, modalsProps)
  const { isPasswordValid, validationComponent } = usePasswordValidation({
    password: values.newTradingAccountPassword,
  })

  const handleSetVirtualDeposit = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue('virtualDeposit', event.target.value)
  }

  const handleFormSubmit = () => {
    setIsLoading(true)
    handleSubmit()
  }
  const isSelectedPlatformTMTorTV =
    isTMTPlatformType(values?.platformType?.id) ||
    isTradingViewPlatformType(values?.platformType?.id)

  const handleLeverageChange = (option: NameDto) => {
    const selectedLeverage = leverages.find((lev) => lev.id.toString() === option.id.toString())
    if (selectedLeverage) {
      setFieldValue('leverageType', selectedLeverage)
    }
  }

  const handleClassificationChange = (option: NameDto) => {
    const selectedClassification = modalsProps.classificationTypes.find(
      (classification) => classification.id.toString() === option.id.toString()
    )
    if (selectedClassification) {
      setFieldValue('classificationType', selectedClassification)
    }
  }

  const shouldShowPasswordField = !isSelectedPlatformTMTorTV || !props.hasTMTOrTVAccount
  const isPasswordOk = shouldShowPasswordField ? isPasswordValid : true
  return (
    <Loading showLoadingIcon isLoading={isLoading}>
      <FormTemplate
        title={t('Trading Account.Add Demo Trading Account')}
        subtitle={<div className={styles.subTitle}>{subtitle}</div>}
        goBack={cancelModal.open}
      >
        <Form onSubmit={handleFormSubmit}>
          <FieldWrapper>
            <PopoverInput
              name='platformType.name'
              label={t('Trading Account.Trading Platform')}
              value={values.platformType?.id}
              options={values.platformType ? [values.platformType] : []}
              required
              onClick={modalsProps.tradingPlatformModal.open}
            />
            <PopoverInput
              name='accountCurrency.id'
              label={t('Trading Account.Account Currency')}
              value={values.accountCurrency?.id}
              required
              disabled={isCurrencyDisabled}
              options={modalsProps.currencies.map((curr) => ({ id: curr.id, name: curr.id }))}
              onChange={(option) => {
                setValues({
                  ...values,
                  accountCurrency: option,
                  accountType: undefined,
                  leverageType: undefined,
                })
              }}
              displayValue='id'
            />
            <PopoverInput
              name='accountType.name'
              label={t('Trading Account.Account Type')}
              value={values.accountType?.id}
              options={modalsProps.accountTypes}
              required
              disabled={disabledFields().type}
              onChange={(option) => setFieldValue('accountType', option)}
            />
            <PopoverInput
              name='leverageType.name'
              label={t('Trading Account.Leverage')}
              value={values.leverageType?.id}
              options={leverageOptions}
              required
              disabled={isLeverageDisabled}
              hint={
                isLeverageTypeDynamic(values.leverageType?.id) ? (
                  <span
                    onClick={(e) => {
                      e.stopPropagation()
                      dynamicLeverageModal.open()
                    }}
                    className='is-link'
                  >
                    {t('Trading Account.Leverage Information')}
                  </span>
                ) : undefined
              }
              onChange={handleLeverageChange}
            />
            {isClassificationTypeVisible && (
              <PopoverInput
                name='classificationType.name'
                label={t('Trading Account.Classification')}
                required
                value={values.classificationType?.id}
                options={modalsProps.classificationTypes}
                disabled={disabledFields().classification}
                onChange={handleClassificationChange}
              />
            )}
            <NumericFormField
              name='virtualDeposit'
              label={t('Trading Account.Virtual Deposit')}
              required
              disabled={disabledFields().deposit}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleSetVirtualDeposit(event)
              }
            />
            {shouldShowPasswordField && (
              <>
                <PasswordFormField
                  name='newTradingAccountPassword'
                  label={t('Trading Account.New Trading Account Password')}
                  showLabel
                  type='password'
                  hint={
                    isSelectedPlatformTMTorTV &&
                    t(
                      'Trading Account.Once set, the same password will be shared across all Tickmill Trader and TradingView accounts'
                    )
                  }
                  required
                />
                {validationComponent}
              </>
            )}

            <TransactionFormActionButtonTemplate>
              <Button.Secondary size='lg' onClick={() => cancelModal.open()}>
                {t('Cancel')}
              </Button.Secondary>
              <Button.Primary
                size='lg'
                type='submit'
                loading={isSubmitting}
                disabled={!(isPasswordOk && isValid && dirty && !isSubmitting)}
              >
                {t('Confirm')}
              </Button.Primary>
            </TransactionFormActionButtonTemplate>
          </FieldWrapper>
        </Form>
      </FormTemplate>
    </Loading>
  )
}

function dataToSubmit(
  values: CreateDemoTradingAccountFormValues,
  entity: TickmillCompaniesEnum
): FormSubmitValues<CreateDemoTradingAccount> {
  const classificationTypeId =
    entity !== TickmillCompaniesEnum.TICKMILL_SC && entity !== TickmillCompaniesEnum.TICKMILL_AS
      ? values.classificationType?.id!
      : null

  return {
    leverageTypeId: values.leverageType?.id!,
    platformTypeId: values.platformType?.id!,
    tradingAccountTypeId: values.accountType?.id!,
    accountCurrencyId: values.accountCurrency?.id!,
    classificationTypeId,
    deposit: values.virtualDeposit,
    password: values.newTradingAccountPassword || null,
  }
}

export const CreateDemoTradingAccountForm = withFormik<
  OuterProps,
  CreateDemoTradingAccountFormValues
>({
  mapPropsToValues: (props) => {
    const { selectedPlatformType } = props

    const virtualDeposit = selectedPlatformType
      ? isTradingViewPlatformType(selectedPlatformType.id) ||
        isTMTPlatformType(selectedPlatformType.id)
        ? DEFAULT_VIRTUAL_DEPOSIT
        : 0
      : 0

    return {
      leverageType: undefined,
      platformType: selectedPlatformType,
      accountType: undefined,
      classificationType: undefined,
      accountCurrency: undefined,
      newTradingAccountPassword: '',
      virtualDeposit,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values, props.entity))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, { entity }) => {
    const errors: FormikErrors<CreateDemoTradingAccountFormValues> = {}
    if (!values.accountType) {
      errors.accountType = t('Validation.Required')
    }
    if (values.virtualDeposit < 100) {
      errors.virtualDeposit = t(
        'Validation.Virtual Deposit cannot be smaller than 100 or greater than 5,000,000'
      )
    }
    if (values.virtualDeposit > 5000000) {
      errors.virtualDeposit = t(
        'Validation.Virtual Deposit cannot be smaller than 100 or greater than 5,000,000'
      )
    }
    if (!values.platformType) {
      errors.platformType = t('Validation.Required')
    }
    if (!values.leverageType) {
      errors.leverageType = t('Validation.Required')
    }
    if (!values.virtualDeposit) {
      errors.virtualDeposit = t('Validation.Required')
    }
    if (!values.classificationType) {
      if (
        entity !== TickmillCompaniesEnum.TICKMILL_SC &&
        entity !== TickmillCompaniesEnum.TICKMILL_AS
      ) {
        errors.classificationType = t('Validation.Required')
      }
    }
    return errors
  },
  enableReinitialize: true,
  isInitialValid: false,
})(CreateDemoTradingAccountFormUI)
